import type { FocusEventHandler } from 'react'
import React, { useRef } from 'react'

import { useSlider } from '@react-aria/slider'
import { useSliderState } from '@react-stately/slider'
import classnames from 'classnames'

import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './Slider.module.scss'
import { SliderThumb } from './SliderThumb'

/*
 * If used in an SSR application, this component requires a react-aria SSR provider so that server-side generated IDs match client side
 * See https://react-spectrum.adobe.com/react-aria/ssr.html for details
 */
export const Slider = <T extends number | number[]>({
  className,
  onChangeEnd,
  minValue = 0,
  maxValue = 100,
  step = 1,
  defaultValue,
  labelFormatter = ({ value }) => value.toFixed(2).toString(),
  'aria-labelledby': ariaLabelledBy,
  value,
  onChange,
  onFocus,
  ...rest
}: SliderProps<T>) => {
  const trackRef = useRef<HTMLDivElement>(null)

  const state = useSliderState({
    onChangeEnd,
    minValue,
    maxValue,
    step,
    defaultValue,
    // NumberFormatter is not used for formatting but is required by useSliderState
    numberFormatter: new Intl.NumberFormat(),
    value,
    onChange,
    ...rest,
  })

  // TS errors below are safely suppressed as they do not indicate a real issue
  // This does not error in a simplified sandbox example and behaves correctly
  // therefore seems to be an unknown issue related to external packages
  const { groupProps, trackProps, outputProps } = useSlider(
    {
      onChangeEnd,
      onChange,
      minValue,
      maxValue,
      step,
      defaultValue,
      'aria-labelledby': ariaLabelledBy,
      value,
      ...rest,
    },
    state,
    trackRef
  )

  const isDualThumb = state.getThumbValue(1) !== undefined

  return (
    <div
      {...groupProps}
      className={classnames(styles.sliderWrapper, className)}
      data-testid="slider-wrapper"
    >
      <output
        className={styles.output}
        {...outputProps}
        data-testid="slider-output"
      >
        {`${labelFormatter({
          value: state.getThumbValue(0),
          type: 'current',
        })} ${
          isDualThumb
            ? `- ${labelFormatter({
                value: state.getThumbValue(1),
                type: 'current',
              })}`
            : ''
        }`}
      </output>
      <div className={styles.trackWrapper}>
        <div {...trackProps} ref={trackRef} className={styles.track}>
          <SliderThumb
            index={0}
            state={state}
            trackRef={trackRef}
            labelFormatter={labelFormatter}
            aria-labelledby={ariaLabelledBy}
            onFocus={onFocus}
          />
          <div
            className={styles.trackValueBar}
            style={
              isDualThumb
                ? {
                    left: `${state.getThumbPercent(0) * 100}%`,
                    width: `calc(${
                      (state.getThumbPercent(1) - state.getThumbPercent(0)) *
                      100
                    }% + 12px)`,
                  }
                : {
                    left: '0%',
                    width: `calc(${state.getThumbPercent(0) * 100}% + 12px)`,
                  }
            }
          />
          {isDualThumb && (
            <SliderThumb
              index={1}
              state={state}
              trackRef={trackRef}
              labelFormatter={labelFormatter}
              aria-labelledby={ariaLabelledBy}
              onFocus={onFocus}
            />
          )}
        </div>
      </div>
      <div
        className={styles.minMaxLabels}
        aria-hidden="true"
        data-testid="slider-min-max-labels"
      >
        <TypographyV2
          tag="span"
          textStyle="sb-text-interface-body-small-regular"
        >
          {labelFormatter({ value: minValue, type: 'min' })}
        </TypographyV2>
        <TypographyV2
          tag="span"
          textStyle="sb-text-interface-body-small-regular"
        >
          {labelFormatter({ value: maxValue, type: 'max' })}
        </TypographyV2>
      </div>
    </div>
  )
}

///////// IMPLEMENTATION /////////
export type SliderProps<SliderValue = number | number[]> = {
  className?: string
  /**
   * The slider's minimum value.
   * Defaults to 0.
   */
  minValue?: number
  /**
   * The slider's maximum value.
   * Defaults to 100.
   */
  maxValue?: number
  /**
   * The slider's step value.
   * Defaults to 1.
   */
  step?: number
  /**
   * Optional function to format displayed numbers.
   * Defaults to formatting with 2 decimal places.
   */
  labelFormatter?: LabelFormatter
  /**
   * An 'aria-label' or 'aria-labelledby' prop must be provided.
   */
  'aria-label'?: string
  'aria-labelledby'?: string
  /**
   * The slider's thumb starting position(s). Pass a number for a single thumb slider,
   * or an array of two numbers for a dual thumb slider.
   * Defaults to 0.
   */
  defaultValue?: SliderValue
  onFocus?: FocusEventHandler<HTMLInputElement>
  onChangeEnd?: (value: SliderValue) => void
  /**
   * To optionally use Slider as a controlled component, you can provide a value
   * from the parent component's state and an onChange handler. defaultValue will be ignored.
   */
  onChange?: (value: SliderValue) => void
  value?: SliderValue
}

export type LabelFormatter = (args: {
  value: number
  type: 'min' | 'max' | 'current'
}) => string
